import { extendTheme } from "@smoothie-framework/ui-core/theme";
import type { Theme } from "@smoothie-framework/ui-core/types";

const neutral = {
  50: "#fafaf9",
  100: "#f5f5f4",
  200: "#e8e5e3",
  300: "#d7d3d0",
  400: "#aaa39c",
  500: "#736b63",
  600: "#59544c",
  700: "#46413a",
  800: "#2a2523",
  900: "#1d1916",
  950: "#0d0a08",
} as const;

const peach = {
  50: "#fef5f2",
  100: "#ffe9e1",
  200: "#ffd7c8",
  300: "#ffac8e",
  400: "#fd926c",
  500: "#f56f3e",
  600: "#e25420",
  700: "#be4317",
  800: "#9d3b17",
  900: "#82361a",
  950: "#471908",
} as const;

const rose = {
  50: "#fff1f3",
  100: "#ffdce2",
  200: "#f6abb7",
  300: "#f28d9f",
  400: "#e64d6c",
  500: "#d83e5f",
  600: "#d12d56",
  700: "#b92348",
  800: "#931d3b",
  900: "#721a32",
} as const;

const sage = {
  50: "#edf7f2",
  100: "#d9eee5",
  200: "#b5ddca",
  300: "#8fcab0",
  400: "#5f927d",
  500: "#477461",
  600: "#385d4e",
  700: "#2e4b40",
  800: "#273d35",
  900: "#22332d",
} as const;

const textStyles = {
  display: {
    base: {
      fontFamily: "heading",
      fontSize: "2.25rem",
      fontWeight: "medium",
      lineHeight: "2.8125rem",
      letterSpacing: "-0.05625rem",
    },
    md: {
      fontSize: "3.75rem",
      lineHeight: "4.6875rem",
      letterSpacing: "-0.09375rem",
    },
  },
  h1: {
    base: {
      fontFamily: "heading",
      fontSize: "1.875rem",
      fontWeight: "medium",
      lineHeight: "2.34375rem",
      letterSpacing: "0",
    },
    md: {
      fontSize: "3rem",
      lineHeight: "3.75rem",
      letterSpacing: "-0.075rem",
    },
  },
  h2: {
    base: {
      fontFamily: "heading",
      fontSize: "1.875rem",
      fontWeight: "medium",
      lineHeight: "2.34375rem",
      letterSpacing: "0",
    },
    md: {
      fontSize: "2.25rem",
      lineHeight: "2.8125rem",
      letterSpacing: "-0.05625rem",
    },
  },
  h3: {
    base: {
      fontFamily: "heading",
      fontSize: "1.125rem",
      fontWeight: "medium",
      lineHeight: "1.6875rem",
      letterSpacing: "0",
    },
    md: {
      fontSize: "1.5rem",
      lineHeight: "2.0625rem",
    },
  },
  body: {
    base: {
      fontFamily: "body",
      fontSize: "1rem",
      fontWeight: "normal",
      lineHeight: "1.5rem",
      letterSpacing: "0",
    },
    md: {
      fontSize: "1.125rem",
      lineHeight: "1.6875rem",
    },
  },
  "body-sm": {
    base: {
      fontFamily: "body",
      fontSize: "0.875rem",
      fontWeight: "normal",
      lineHeight: "1.203125rem",
      letterSpacing: "0",
    },
    md: {
      fontSize: "1rem",
      lineHeight: "1.5rem",
    },
  },
  label: {
    base: {
      fontFamily: "body",
      fontSize: "0.75rem",
      fontWeight: "medium",
      lineHeight: "1.03125rem",
      letterSpacing: "0",
    },
    md: {
      fontSize: "0.875rem",
      lineHeight: "1.203125rem",
    },
  },
  button: {
    base: {
      fontFamily: "body",
      fontSize: "0.75rem",
      fontWeight: "semibold",
      lineHeight: "1.03125rem",
      letterSpacing: "0",
    },
    md: {
      fontSize: "0.875rem",
      lineHeight: "1.203125rem",
    },
  },
  caption: {
    base: {
      fontFamily: "body",
      fontSize: "0.75rem",
      fontWeight: "normal",
      lineHeight: "1.03125rem",
      letterSpacing: "0",
    },
  },
  meta: {
    base: {
      fontFamily: "mono",
      fontSize: "0.75rem",
      fontWeight: "normal",
      lineHeight: "1.03125rem",
      letterSpacing: "0",
    },
  },
  "preview-title": {
    base: {
      fontFamily: "body",
      fontSize: "1.25rem",
      fontWeight: "medium",
      lineHeight: "1.875rem",
      letterSpacing: "0",
    },
    md: {
      fontSize: "1.5rem",
      lineHeight: "2.0625rem",
    },
  },
  "preview-emphasis": {
    base: {
      fontFamily: "body",
      fontSize: "0.875rem",
      fontWeight: "normal",
      lineHeight: "1.203125rem",
      letterSpacing: "0",
    },
    md: {
      fontSize: "1.125rem",
      lineHeight: "1.6875rem",
    },
  },
  "preview-body": {
    base: {
      fontFamily: "body",
      fontSize: "0.75rem",
      fontWeight: "normal",
      lineHeight: "1.03125rem",
      letterSpacing: "0",
    },
    md: {
      fontSize: "0.875rem",
      lineHeight: "1.203125rem",
    },
  },
  "preview-label": {
    base: {
      fontFamily: "mono",
      fontSize: "0.625rem",
      fontWeight: "normal",
      lineHeight: "0.78125rem",
      letterSpacing: "0",
    },
  },
  "brand-navigation": {
    base: {
      fontFamily: '"Syne", system-ui, sans-serif',
      fontSize: "1rem",
      fontWeight: "extrabold",
      lineHeight: "1.5rem",
      letterSpacing: "0",
    },
    md: {
      fontSize: "1.25rem",
      lineHeight: "1.875rem",
    },
  },
  "brand-footer": {
    base: {
      fontFamily: '"Syne", system-ui, sans-serif',
      fontSize: "1.875rem",
      fontWeight: "extrabold",
      lineHeight: "2.34375rem",
      letterSpacing: "0",
    },
    md: {
      fontSize: "2.25rem",
      lineHeight: "2.8125rem",
    },
  },
} as const;

export const zequelsTheme: Theme = extendTheme(
  {
    typography: {
      fontFamily: {
        body: '"Work Sans", system-ui, sans-serif',
        heading: '"Work Sans", system-ui, sans-serif',
        mono: '"IBM Plex Sans", system-ui, sans-serif',
      },
    },
    textStyles,
    radii: {
      sm: 8,
      md: 16,
      lg: 24,
      xl: 28,
    },
    shadows: {
      md: "0 8px 24px rgba(0, 0, 0, 0.22)",
      lg: "0 22px 40px rgba(0, 0, 0, 0.18)",
      xl: "0 32px 30px rgba(0, 0, 0, 0.8)",
    },
    colors: {
      neutral,
      peach,
      rose,
      sage,

      // Dark semantic colors from the Zequels Figma token collection.
      accent: "{colors.peach.300}",
      accentSubtle: "{colors.peach.200}",
      accentEmphasized: "{colors.peach.100}",
      accentMuted: "{colors.peach.950}",
      neutralDefault: "{colors.neutral.700}",
      neutralSubtle: "{colors.neutral.800}",
      neutralEmphasized: "{colors.neutral.500}",

      background: "{colors.neutral.950}",
      surface1: "{colors.neutral.900}",
      surface2: "{colors.neutral.800}",
      surface3: "{colors.neutral.700}",
      backgroundAlpha32: "{colors.background/32}",
      surface1Alpha32: "{colors.surface1/32}",
      surface2Alpha32: "{colors.surface2/32}",
      surface3Alpha32: "{colors.surface3/32}",
      surfaceInverse: "{colors.peach.50}",
      surfaceInverseAlpha32: "{colors.surfaceInverse/32}",
      surfaceOverlay: "{colors.surface1}",

      text: "{colors.neutral.50}",
      textSubtle: "{colors.neutral.300}",
      textDisabled: "{colors.neutral.500}",
      textInverse: "{colors.neutral.900}",
      textOnOverlay: "#FFFFFF",
      textDark: "{colors.neutral.900}",
      textLight: "{colors.neutral.50}",

      borderSubtle: "{colors.neutral.800}",
      border: "{colors.neutral.700}",
      borderStrong: "{colors.neutral.500}",
      borderFocus: "{colors.peach.300}",

      brandPeach: "{colors.peach.300}",
      brandRose: "{colors.rose.300}",
      brandOnAccent: "{colors.neutral.950}",
      overlayScrim: "#000000",
      mediaOnImage: "#FFFFFF",

      // Status tokens use the Zequels dark-mode overrides from Figma.
      success: "{colors.sage.500}",
      successSubtle: "{colors.sage.400}",
      successEmphasized: "{colors.sage.600}",
      successMuted: "{colors.sage.100}",
      error: "{colors.rose.600}",
      errorSubtle: "{colors.rose.500}",
      errorEmphasized: "{colors.rose.700}",
      errorMuted: "{colors.rose.100}",
    },
    tokens: {
      "layout.contentMax": "90rem",
      "brand.gradient":
        "linear-gradient(100deg, {colors.peach.400}, {colors.rose.300})",
      "button.primary.bg": "{brand.gradient}",
      "button.primary.hover.bg": "{brand.gradient}",
      "button.primary.color": "{colors.brandOnAccent}",
      "button.primary.focus.outline": "{colors.borderFocus}",
      "button.base.fontFamily": "{typography.fontFamily.body}",
      "button.base.fontWeight": "{typography.fontWeight.semibold}",
      "button.base.textTransform": "none",
      "button.size.sm.height": "2rem",
      "button.size.sm.paddingLeft": "{spacing.3}",
      "button.size.sm.paddingRight": "{spacing.3}",
      "button.size.sm.borderRadius": "{radii.full}",
      "button.size.sm.fontSize": "0.75rem",
      "button.size.sm.gap": "{spacing.2}",
      "button.size.sm.iconPaddingLeft": "{spacing.2}",
      "button.size.sm.iconPaddingRight": "{spacing.2}",
      "button.size.sm.iconPaddingBoth": "{spacing.2}",
      "button.size.md.height": "2.5rem",
      "button.size.md.paddingLeft": "{spacing.4}",
      "button.size.md.paddingRight": "{spacing.4}",
      "button.size.md.borderRadius": "{radii.full}",
      "button.size.md.fontSize": "0.875rem",
      "button.size.md.gap": "{spacing.2}",
      "button.size.md.iconPaddingLeft": "{spacing.3}",
      "button.size.md.iconPaddingRight": "{spacing.3}",
      "button.size.md.iconPaddingBoth": "{spacing.3}",
      "button.size.lg.height": "3rem",
      "button.size.lg.paddingLeft": "{spacing.5}",
      "button.size.lg.paddingRight": "{spacing.5}",
      "button.size.lg.borderRadius": "{radii.full}",
      "button.size.lg.fontSize": "0.875rem",
      "button.size.lg.gap": "{spacing.2}",
      "button.size.lg.iconPaddingLeft": "{spacing.4}",
      "button.size.lg.iconPaddingRight": "{spacing.4}",
      "button.size.lg.iconPaddingBoth": "{spacing.4}",
    },
  },
  { validate: false, strict: false },
).theme;
